// Shared vocabulary for the member self-service screens (My Home, My Bills,
// My Payments). Kept in one place so a bill reads the same on the overview
// tile, in the list and on the detail page.

export const INVOICE_STATUS_STYLE = {
    PAID: 'tw-bg-emerald-100 tw-text-emerald-700',
    PARTIAL: 'tw-bg-amber-100 tw-text-amber-700',
    POSTED: 'tw-bg-sky-100 tw-text-sky-700',
    DRAFT: 'tw-bg-gray-100 tw-text-gray-600',
    VOID: 'tw-bg-rose-100 tw-text-rose-700',
    CANCELLED: 'tw-bg-gray-100 tw-text-gray-600',
    OVERDUE: 'tw-bg-rose-100 tw-text-rose-700',
};

export const RECEIPT_STATUS_STYLE = {
    CLEARED: 'tw-bg-emerald-100 tw-text-emerald-700',
    PENDING: 'tw-bg-amber-100 tw-text-amber-700',
    BOUNCED: 'tw-bg-rose-100 tw-text-rose-700',
    CANCELLED: 'tw-bg-gray-100 tw-text-gray-600',
};

export const PAYMENT_MODE_LABEL = {
    CASH: 'Cash',
    CHEQUE: 'Cheque',
    NEFT: 'NEFT',
    RTGS: 'RTGS',
    IMPS: 'IMPS',
    UPI: 'UPI',
    CARD: 'Card',
    ONLINE: 'Online',
};

// Charge heads the invoice engine adds itself. They are real lines on the bill,
// but a member reads them differently from the maintenance charges, so the
// detail screen groups them below the charges.
export const SYSTEM_HEAD_CODES = ['CGST', 'SGST', 'IGST', 'PAR', 'DNR', 'INT', 'RND', 'ADJ'];

export const humanize = (s) =>
    (s || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

export const fmtAmount = (amount) =>
    parseFloat(amount || 0).toLocaleString('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

export const fmtCurrency = (amount) => `Rs ${fmtAmount(amount)}`;

export const fmtDate = (d) =>
    d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// "Aug 2026", or "Jul 2026 - Sep 2026" for a quarterly bill.
export const fmtPeriod = (start, end) => {
    if (!start) return '-';
    const s = new Date(start);
    const startStr = `${MONTHS[s.getMonth()]} ${s.getFullYear()}`;
    if (!end) return startStr;
    const e = new Date(end);
    const endStr = `${MONTHS[e.getMonth()]} ${e.getFullYear()}`;
    return startStr === endStr ? startStr : `${startStr} - ${endStr}`;
};

// A bill is overdue when money is still owed after its due date. The backend
// keeps `status` at POSTED/PARTIAL in that case, so the member screens derive
// the overdue flag rather than expecting a status for it.
export const isOverdue = (invoice) =>
    parseFloat(invoice?.balance_due || 0) > 0 &&
    !!invoice?.due_date &&
    new Date(invoice.due_date) < new Date();

export const daysOverdue = (invoice) => {
    if (!isOverdue(invoice)) return 0;
    const diff = Date.now() - new Date(invoice.due_date).getTime();
    return Math.max(0, Math.floor(diff / 86400000));
};

// What the member should see on a bill row: the derived overdue state wins over
// the bookkeeping status, which is what they actually care about.
export const billStatusLabel = (invoice) => {
    if (isOverdue(invoice)) return 'OVERDUE';
    if (parseFloat(invoice?.balance_due || 0) <= 0 && invoice?.status !== 'VOID') return 'PAID';
    return invoice?.status || '-';
};

// Flats are labelled "A-1204" across the finance screens (wing title, then
// flat number); fall back to the flat number alone when the wing isn't joined.
export const flatLabel = (flat) => {
    if (!flat) return '-';
    const wing = flat.wing?.title;
    const unit = flat.flat_number || flat.flat_no || '-';
    return wing ? `${wing}-${unit}` : unit;
};
